import { Component, ChangeDetectionStrategy, signal, inject, OnInit, OnDestroy, effect } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { CommonModule } from '@angular/common';
import { NotificationService } from '../../services/notification.service';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';

@Component({
  selector: 'app-pin-login',
  standalone: true,
  templateUrl: './pin-login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, TranslatePipe, CommonModule, ConfirmationModalComponent],
})
export class PinLoginComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private notificationService = inject(NotificationService);
  private router: Router = inject(Router);

  pin = signal('');
  error = signal('');
  isLoading = signal(false);
  
  isVerifyingSession = signal(true); 
  
  pinDisplay = signal('------');
  isDeviceSetup = signal(false);
  showResetSuggestion = signal(false);

  private safetyTimer: any;
  keypad = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0', '<'];

  confirmModalState = signal({
    isOpen: false,
    title: '',
    message: '',
    confirmButtonText: 'Confirm',
    confirmButtonClass: 'bg-red-600 hover:bg-red-700',
    action: null as (() => Promise<void>) | null
  });

  constructor() {
    effect(() => {
      const user = this.authService.currentUser();
      if (user) {
          this.navigateToDashboard(user);
      }
    });
  }

  async ngOnInit(): Promise<void> {
      // Safety Timeout: Force UI unlock after 8 seconds if session check hangs
      this.safetyTimer = setTimeout(() => {
          if (this.isVerifyingSession()) {
              console.warn("[PinLogin] Safety timeout hit. Unlocking UI.");
              this.isVerifyingSession.set(false);
              this.showResetSuggestion.set(true);
          }
      }, 8000);

      const fullUrl = window.location.href;
      const hasOAuthParams = 
        fullUrl.includes('access_token=') || 
        fullUrl.includes('refresh_token=') || 
        fullUrl.includes('code=') ||
        fullUrl.includes('error_description=');

      if (hasOAuthParams) {
          this.isVerifyingSession.set(true);
          return; 
      }

      const sessionFound = await this.authService.checkCurrentSession();
      
      if (!sessionFound) {
          this.clearSafetyTimer();
          this.isVerifyingSession.set(false);
          
          const boundStore = this.authService.getBoundStoreId();
          this.isDeviceSetup.set(!!boundStore);
          
          if (!boundStore && localStorage.getItem('custom_pin_token')) {
              this.showResetSuggestion.set(true);
          }
      }
  }

  ngOnDestroy() {
      this.clearSafetyTimer();
  }

  private clearSafetyTimer() {
      if (this.safetyTimer) {
          clearTimeout(this.safetyTimer);
          this.safetyTimer = null;
      }
  }

  private navigateToDashboard(user: any) {
      this.clearSafetyTimer();
      if (user.role === 'platform_admin') {
          this.router.navigate(['/platform']);
      } else {
          const store = this.authService.currentStore();
          if (user.role === 'owner' && store && store.setupCompleted === false) {
             this.router.navigate(['/onboarding']);
          } else {
             this.router.navigate(['/admin/customers']);
          }
      }
  }

  async handleKeyPress(key: string): Promise<void> {
    if (this.isLoading() || this.isVerifyingSession()) return;
    
    if (!this.isDeviceSetup()) {
        if (key === 'C') this.error.set("Please login with Email first.");
        return;
    }

    this.error.set('');
    this.showResetSuggestion.set(false); 

    if (key === 'C') {
      this.pin.set('');
    } else if (key === '<') {
      this.pin.update(p => p.slice(0, -1));
    } else {
      if (this.pin().length < 6) {
        this.pin.update(p => p + key);
      }
    }
    
    this.updatePinDisplay();

    if (this.pin().length === 6) {
      await this.attemptLogin();
    }
  }

  async attemptLogin() {
      this.isLoading.set(true);
      try {
          const result = await this.authService.loginWithPin(this.pin());
          if (!result.success) {
              this.handleError(result.error || 'Unknown error');
              if ((result.error || '').toLowerCase().includes('store')) {
                  this.showResetSuggestion.set(true);
              }
          }
      } catch (e) {
          this.handleError('System Error');
      } finally {
          this.isLoading.set(false);
      }
  }

  handleError(msg: string) {
      this.error.set(msg);
      setTimeout(() => {
          this.pin.set('');
          this.updatePinDisplay();
          if (!this.showResetSuggestion()) this.error.set('');
      }, 2000); 
  }
  
  private updatePinDisplay(): void {
      const p = this.pin();
      this.pinDisplay.set('*'.repeat(p.length) + '-'.repeat(6 - p.length));
  }

  resetDeviceData(): void {
      this.confirmModalState.set({
        isOpen: true,
        title: 'ยืนยันการล้างข้อมูลเครื่อง',
        message: 'ข้อมูลการตั้งค่าเดิมในเครื่องนี้จะถูกล้างทั้งหมดเพื่อแก้ไขปัญหาการเข้าถึง ยืนยันหรือไม่?',
        confirmButtonText: 'ล้างข้อมูล (Reset)',
        confirmButtonClass: 'bg-red-600 hover:bg-red-700',
        action: async () => {
           this.isLoading.set(true);
            
            // ล้างข้อมูลทันที
            localStorage.clear();
            sessionStorage.clear();
            
            // แจ้งเตือนให้ทราบผล
            this.notificationService.showSuccess('ล้างข้อมูลสำเร็จ ระบบจะเริ่มใหม่ทันที');
            
            // สั่ง Hard Reset ผ่าน Service และ Reload หน้าจอ
            await this.authService.hardReset();
        }
    });
  }

  closeConfirmModal() {
    this.confirmModalState.update(s => ({ ...s, isOpen: false }));
  }

  async onConfirmAction() {
    const action = this.confirmModalState().action;
    if (action) await action();
    this.closeConfirmModal();
  }
}